import React, { useState, useEffect, useContext } from 'react';
import './CreateAvatar.css';
// Nous allons remplacer l'import des styles statiques
// import STYLE_PROMPTS from '../../data/stylePrompts';
import api from '../../api';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../Login/AuthContext';
import { useToast } from '../UI/ToastContext';

const CreateAvatar = ({ onStylesSelected, preselectedStyles = [], userId = 1 }) => {
  const [selectedStyles, setSelectedStyles] = useState(preselectedStyles);
  const [styles, setStyles] = useState([]);
  const [styleMap, setStyleMap] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [totalTokenCost, setTotalTokenCost] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate(); // Hook pour la navigation programmatique
  const { showSuccess, showError } = useToast();

  // Charger les styles depuis l'API au lieu du fichier statique
  useEffect(() => {
    const fetchStyles = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/aivatar/styles');
        if (response.data.success) {
          const stylesArray = response.data.styles;
          setStyles(stylesArray);
          
          // Créer aussi un map pour un accès facile par ID
          const stylesById = {};
          stylesArray.forEach(style => {
            // Assurer que l'ID est un nombre
            const styleId = parseInt(style.id, 10);
            stylesById[styleId] = style;
          });
          setStyleMap(stylesById);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des styles:', error);
        setSubmitError('Impossible de charger les styles. Veuillez réessayer plus tard.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStyles();
  }, []);

  // Update token cost whenever selected styles change
  useEffect(() => {
    let cost = 0;
    selectedStyles.forEach(styleId => {
      const style = styleMap[styleId];
      if (style) {
        cost += style.token_cost;
      }
    });
    setTotalTokenCost(cost);
    
    // Call the parent component's callback with selected styles
    if (onStylesSelected) {
      onStylesSelected(selectedStyles);
    }
  }, [selectedStyles, onStylesSelected, styleMap]);

  // Check if user has enough tokens
  const hasEnoughTokens = () => {
    if (!user) return false;
    const userTotalTokens = (user.tokens_ltd || 0) + (user.tokens_subscription || 0);
    return userTotalTokens >= totalTokenCost;
  };

  const getTokensError = () => {
    if (!user) return "Unable to verify token balance";
    const userTotalTokens = (user.tokens_ltd || 0) + (user.tokens_subscription || 0);
    return `Not enough tokens: you need ${totalTokenCost} but only have ${userTotalTokens} available.`;
  };

  const handleStyleToggle = (styleId) => {
    // S'assurer que styleId est un nombre
    const numericStyleId = parseInt(styleId, 10);
    
    setSelectedStyles(prev => {
      if (prev.includes(numericStyleId)) {
        return prev.filter(id => id !== numericStyleId);
      } else {
        return [...prev, numericStyleId];
      }
    });
  };

  const getStylesToDisplay = () => {
    if (activeTab === 'all') {
      return styles;
    }
    // We're not filtering by 'realistic' and 'fantasy' for now as requested
    return styles;
  };

  const getStyleById = (styleId) => {
    return styleMap[styleId];
  };

  const handleSubmit = async () => {
    if (selectedStyles.length === 0) {
      setSubmitError("Please select at least one style");
      return;
    }

    // Check if user has enough tokens before submitting
    if (!hasEnoughTokens()) {
      setSubmitError(getTokensError());
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // S'assurer que tous les IDs sont des nombres
      const numericStyleIds = selectedStyles.map(id => parseInt(id, 10));
      
      // Create data for the request
      const data = {
        styles: numericStyleIds,
        style_prompts: numericStyleIds.map(styleId => {
          const style = styleMap[styleId];
          return style ? style.prompt : '';
        }),
        use_reference_elements: true
      };

      console.log("Sending style IDs:", numericStyleIds);
      
      const response = await api.post('/aivatar/create_tasks_batch', data);
      
      // Utiliser un message en anglais
      showSuccess(`${response.data.message || 'Avatar generation tasks created successfully!'}`);
      
      // Rediriger vers la galerie après une génération réussie
      navigate('/app/gallery');
      
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.error === "Not enough tokens") {
        setSubmitError(`${error.response.data.message}. Please deselect some styles or purchase more tokens.`);
        showError(`${error.response.data.message}. Please deselect some styles or purchase more tokens.`);
      } else {
        setSubmitError(error.response?.data?.error || error.message);
        showError(error.response?.data?.error || error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-avatar">
      <div className="create-avatar__main">
        <div className="create-avatar__header">
          <h2>Create Your Avatar</h2>
          <p>Choose up to 10 styles to apply to your avatar</p>
        </div>

        <div className="create-avatar__tabs">
          <button 
            className={`create-avatar__tab ${activeTab === 'all' ? 'active' : ''}`} 
            onClick={() => setActiveTab('all')}
          >
            All Styles
          </button>
        </div>

        {isLoading ? (
          <div className="create-avatar__loading">
            <p>Loading styles...</p>
          </div>
        ) : (
          <div className="create-avatar__grid">
            {getStylesToDisplay().map(style => (
              <div 
                key={style.id} 
                className={`create-avatar__item ${selectedStyles.includes(style.id) ? 'selected' : ''}`}
                onClick={() => handleStyleToggle(style.id)}
              >
                <div className="create-avatar__image-container">
                  <img 
                    src={style.image_url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`} 
                    alt={style.name} 
                    className="create-avatar__image" 
                  />
                  {selectedStyles.includes(style.id) && (
                    <div className="create-avatar__selected-overlay">
                      <span className="create-avatar__checkmark">✓</span>
                    </div>
                  )}
                </div>
                <div className="create-avatar__details">
                  <h3 className="create-avatar__name">{style.name}</h3>
                  <p className="create-avatar__description">{style.description}</p>
                  <p className="create-avatar__token-cost">{style.token_cost} {style.token_cost === 1 ? 'token' : 'tokens'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="create-avatar__sidebar">
        <div className="create-avatar__summary-panel panel panel-with-header">
          <div className="panel-header">
            <h3>Selected Styles</h3>
          </div>
          <div className="panel-content">
            {selectedStyles.length === 0 ? (
              <p className="create-avatar__empty-message">No styles selected yet</p>
            ) : (
              <>
                <ul className="create-avatar__selected-list">
                  {selectedStyles.map(styleId => {
                    const style = getStyleById(styleId);
                    return style ? (
                      <li key={styleId} className="create-avatar__selected-item">
                        <span>{style.name}</span>
                        <span>{style.token_cost} tokens</span>
                        <button 
                          className="btn-icon create-avatar__remove-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStyleToggle(styleId);
                          }}
                          title="Remove style"
                        >
                          <span>×</span>
                        </button>
                      </li>
                    ) : null;
                  })}
                </ul>
                
                <div className="create-avatar__total">
                  <span>Total</span>
                  <span className={!hasEnoughTokens() ? 'token-error' : ''}>
                    {totalTokenCost} tokens
                  </span>
                </div>

                {!hasEnoughTokens() && (
                  <div className="create-avatar__token-warning">
                    <p>You don't have enough tokens for these styles.</p>
                    <div className="create-avatar__token-actions">
                      <span>Please </span>
                      <button 
                        className="btn-text"
                        onClick={() => setSelectedStyles([])}
                      >
                        deselect styles
                      </button>
                      <span> or </span>
                      <Link to="/pricing" className="btn-text">
                        purchase more tokens
                      </Link>
                    </div>
                  </div>
                )}

                {submitError && (
                  <p className="create-avatar__error text-center">{submitError}</p>
                )}

                <div className="panel-footer text-center">
                  <button 
                    className="btn btn-primary btn-full"
                    onClick={handleSubmit}
                    disabled={isSubmitting || selectedStyles.length === 0 || !hasEnoughTokens()}
                  >
                    {isSubmitting ? 'Creating Avatars...' : 'Create Avatars'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAvatar; 